import React from "react";
import * as ContextMenuPrimitive from "@radix-ui/react-context-menu";
import { useMobileEdgeChromeStore } from "../../stores/mobileEdgeChromeStore";

function ContextMenu({ onOpenChange, ...props }) {
  const handleOpenChange = React.useCallback(
    (open) => {
      useMobileEdgeChromeStore.getState().actions.notifyMenuOverlayOpenChange(open);
      onOpenChange?.(open);
    },
    [onOpenChange]
  );
  return <ContextMenuPrimitive.Root {...props} onOpenChange={handleOpenChange} />;
}
const ContextMenuTrigger = ContextMenuPrimitive.Trigger;
const ContextMenuSub = ContextMenuPrimitive.Sub;

const ContextMenuContent = React.forwardRef(({ children, className = "", ...props }, ref) => {
  return (
    <ContextMenuPrimitive.Portal>
      <ContextMenuPrimitive.Content
        ref={ref}
        className={`
          menu-selection-root
          min-w-[220px] overflow-hidden rounded-md
          border border-gray-600
          p-1 shadow-xl
          animate-in fade-in-0 zoom-in-95
          data-[state=closed]:animate-out
          data-[state=closed]:fade-out-0
          data-[state=closed]:zoom-out-95
          data-[side=bottom]:slide-in-from-top-2
          data-[side=left]:slide-in-from-right-2
          data-[side=right]:slide-in-from-left-2
          data-[side=top]:slide-in-from-bottom-2
          ${className}
        `}
        style={{ backgroundColor: "var(--panel-surface)" }}
        {...props}
      >
        {children}
      </ContextMenuPrimitive.Content>
    </ContextMenuPrimitive.Portal>
  );
});

ContextMenuContent.displayName = "ContextMenuContent";

const ContextMenuSubContent = React.forwardRef(({ children, className = "", ...props }, ref) => {
  return (
    <ContextMenuPrimitive.Portal>
      <ContextMenuPrimitive.SubContent
        ref={ref}
        className={`
          menu-selection-root
          min-w-[220px] overflow-hidden rounded-md
          border border-gray-600
          p-1 shadow-xl
          animate-in fade-in-0 zoom-in-95
          data-[state=closed]:animate-out
          data-[state=closed]:fade-out-0
          data-[state=closed]:zoom-out-95
          data-[side=bottom]:slide-in-from-top-2
          data-[side=left]:slide-in-from-right-2
          data-[side=right]:slide-in-from-left-2
          data-[side=top]:slide-in-from-bottom-2
          ${className}
        `}
        style={{ backgroundColor: "var(--panel-surface)" }}
        {...props}
      >
        {children}
      </ContextMenuPrimitive.SubContent>
    </ContextMenuPrimitive.Portal>
  );
});

ContextMenuSubContent.displayName = "ContextMenuSubContent";

const ContextMenuItem = React.forwardRef(
  ({ children, className = "", icon = null, disabled = false, ...props }, ref) => {
    return (
      <ContextMenuPrimitive.Item
        ref={ref}
        className={`
        relative flex cursor-default select-none items-center
        rounded-sm px-2 py-1.5 text-sm outline-none
        text-gray-200
        disabled:pointer-events-none disabled:opacity-50
        transition-colors duration-150
        ${className}
      `}
        style={{ backgroundColor: "transparent" }}
        onMouseEnter={(e) => {
          if (!disabled) {
            e.target.style.backgroundColor = "var(--panel-surface-hover)";
            e.target.style.color = "white";
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled) {
            e.target.style.backgroundColor = "transparent";
            e.target.style.color = "rgb(229, 231, 235)"; // text-gray-200
          }
        }}
        disabled={disabled}
        {...props}
      >
        {icon && <span className="mr-2 text-gray-400 text-base">{icon}</span>}
        {children}
      </ContextMenuPrimitive.Item>
    );
  }
);

ContextMenuItem.displayName = "ContextMenuItem";

const ContextMenuSubTrigger = React.forwardRef(
  ({ children, className = "", disabled = false, ...props }, ref) => {
    return (
      <ContextMenuPrimitive.SubTrigger
        ref={ref}
        disabled={disabled}
        className={`
          relative flex cursor-default select-none items-center
          rounded-sm px-2 py-1.5 text-sm outline-none
          text-gray-200
          disabled:pointer-events-none disabled:opacity-50
          transition-colors duration-150
          ${className}
        `}
        style={{ backgroundColor: "transparent" }}
        onMouseEnter={(e) => {
          if (!disabled) {
            e.currentTarget.style.backgroundColor = "var(--panel-surface-hover)";
            e.currentTarget.style.color = "white";
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled) {
            e.currentTarget.style.backgroundColor = "transparent";
            e.currentTarget.style.color = "rgb(229, 231, 235)";
          }
        }}
        {...props}
      >
        <span>{children}</span>
        <span className="ml-auto text-xs text-gray-400">▶</span>
      </ContextMenuPrimitive.SubTrigger>
    );
  }
);

ContextMenuSubTrigger.displayName = "ContextMenuSubTrigger";

const ContextMenuSeparator = React.forwardRef(({ className = "", ...props }, ref) => {
  return (
    <ContextMenuPrimitive.Separator
      ref={ref}
      className={`-mx-1 my-1 h-px bg-gray-600 ${className}`}
      {...props}
    />
  );
});

ContextMenuSeparator.displayName = "ContextMenuSeparator";

const ContextMenuLabel = React.forwardRef(({ children, className = "", ...props }, ref) => {
  return (
    <ContextMenuPrimitive.Label
      ref={ref}
      className={`
        px-2 py-1.5 text-sm font-semibold
        text-gray-400 uppercase tracking-wide
        ${className}
      `}
      {...props}
    >
      {children}
    </ContextMenuPrimitive.Label>
  );
});

ContextMenuLabel.displayName = "ContextMenuLabel";

export {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuSub,
  ContextMenuSubTrigger,
  ContextMenuSubContent,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuLabel,
};
